import { Guid } from "guid-typescript";

export const randomString = (length = 11): string => {
  let outString = "";
  const inOptions = "abcdefghijklmnopqrstuvwxyz0123456789";

  for (let i = 0; i < length; i++) {
    outString += inOptions.charAt(Math.floor(Math.random() * inOptions.length));
  }

  return outString;
};

export const htmlEscape = (target?: string | null): string => {
  if (target) {
    return target
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }
  return "";
};

export const htmlUnescape = (target?: string | null): string => {
  if (target) {
    return typeof target !== "string"
      ? target
      : target
          .replace(/&quot;/g, '"')
          .replace(/&apos;/g, "'")
          .replace(/&amp;/g, "&")
          .replace(/&gt;/g, "<")
          .replace(/&lt;/g, ">");
  }
  return "";
};

export const splitOnCamelCase = (text: string, leaveAcronyms: boolean = false): string => {
  if (leaveAcronyms) {
    return text
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Split between lowercase and uppercase
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2"); // Split before last uppercase in sequence
  }
  return text
    .split(/(?=[A-Z])/)
    .map((word) => word.toLowerCase())
    .join(" ");
};

export const toTitleCase = (text?: string, leaveAcronyms: boolean = false): string => {
  if (leaveAcronyms) {
    return text ?? "";
  }
  return (
    text?.replace(/\w\S*/g, (word: string) => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }) ?? ""
  );
};

export const camelToTitleCase = (text: string, leaveAcronyms: boolean = false): string =>
  toTitleCase(splitOnCamelCase(text, leaveAcronyms), leaveAcronyms);

export const boxGuid = (guid?: Guid | string): string => {
  if (guid === undefined) {
    return "";
  }

  return typeof guid === "string" ? guid : guid.toString();
};

export const isValidGuid = (guid: string): boolean => {
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!guid || typeof guid !== "string") {
    return false;
  }

  const normalizedGuid = guid.trim().toLowerCase();

  if (!guidRegex.test(normalizedGuid)) {
    return false;
  }

  return normalizedGuid !== Guid.EMPTY;
};

export const luhnCheck = (value?: string): boolean => {
  const doLuhnChk = (function (arr) {
    return function (ccNum): boolean {
      let len = ccNum.length,
        bit = 1,
        sum = 0,
        val;

      while (len) {
        val = parseInt(ccNum.charAt(--len), 10);
        sum += (bit ^= 1) ? arr[val] : val;
      }

      return sum !== undefined && sum % 10 === 0;
    };
  })([0, 2, 4, 6, 8, 1, 3, 5, 7, 9]);

  if (value && value.trim() !== "" && value.trim().length >= 12) {
    return doLuhnChk(value);
  }

  return false;
};

export const isValidBankRoutingNumber = (value?: string): boolean => {
  const routingCheck = (routingNbr): boolean => {
    let n = 0;
    for (let i = 0; i < routingNbr.length; i += 3) {
      n +=
        parseInt(routingNbr.charAt(i), 10) * 3 +
        parseInt(routingNbr.charAt(i + 1), 10) * 7 +
        parseInt(routingNbr.charAt(i + 2), 10);
    }
    return n !== 0 && n % 10 === 0;
  };

  return value ? routingCheck(value) : false;
};

export const isValidBankAccountNumber = (value?: string): boolean => {
  const testAccount = (acctNum: string): boolean => {
    if (!acctNum) {
      return false;
    }
    const cleanedAccountNumber = acctNum.replace(/\s/g, "");
    return /^[0-9]{5,17}$/.test(cleanedAccountNumber);
  };

  return value ? testAccount(value) : false;
};

export const isAmexCreditCard = (value?: string): boolean => {
  const testCard = (cardNum): boolean => cardNum && cardNum.replace(/\s/g, "").match(/^(?:3[47][0-9]{13})$/);
  return value ? testCard(value) : false;
};

export const isNullOrWhiteSpace = (val?: string | null | void): boolean => {
  if (typeof val !== "string") {
    return true;
  }
  return (val ?? "").trim() === "";
};

export const formatBoolean = (value: boolean): string => (value ? "Yes" : "No");
